import React from 'react';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

interface TimelinePhase {
  title: string;
  tasks: string[];
}

interface RoadmapTimelineProps {
  timeline: {
    days30?: TimelinePhase;
    days60?: TimelinePhase;
    days90?: TimelinePhase;
    days120?: TimelinePhase;
    days180?: TimelinePhase;
  };
}

const RoadmapTimeline: React.FC<RoadmapTimelineProps> = ({ timeline }) => {
  // Support both old (30-60-90) and new (60-120-180) timeline structures
  let phases: Array<{key: string, phase: TimelinePhase | undefined, period: string, color: string}> = [];
  
  if (timeline?.days30) {
    // Old structure (30-60-90 days)
    phases = [
      { key: 'days30', phase: timeline.days30, period: '0-30 Giorni', color: 'bg-blue-500' },
      { key: 'days60', phase: timeline.days60, period: '31-60 Giorni', color: 'bg-yellow-500' },
      { key: 'days90', phase: timeline.days90, period: '61-90 Giorni', color: 'bg-green-500' }
    ].filter(p => p.phase && p.phase.title && p.phase.tasks);
  } else if (timeline) {
    // New structure (60-120-180 days)  
    phases = [
      { key: 'days60', phase: timeline.days60, period: '0-60 Giorni', color: 'bg-blue-500' },
      { key: 'days120', phase: timeline.days120, period: '61-120 Giorni', color: 'bg-yellow-500' },
      { key: 'days180', phase: timeline.days180, period: '121-180 Giorni', color: 'bg-green-500' }
    ].filter(p => p.phase && p.phase.title && p.phase.tasks);
  }
  
  // If no valid phases found, show loading state
  if (phases.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Timeline di Implementazione</h2>
        <div className="text-center py-8">
          <p className="text-gray-500">Caricamento timeline...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Timeline di Implementazione</h2>
      
      <div className="space-y-8">
        {phases.map((phase, index) => (
          <div key={phase.key} className="relative">
            {/* Timeline connector */}
            {index < phases.length - 1 && (
              <div className="absolute left-4 top-12 w-px h-16 bg-gray-300"></div>
            )}
            
            <div className="flex items-start">
              {/* Timeline dot */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full ${phase.color} flex items-center justify-center`}>
                {index === 0 ? (
                  <ClockIcon className="h-4 w-4 text-white" />
                ) : (
                  <CheckCircleIcon className="h-4 w-4 text-white" />
                )}
              </div>
              
              {/* Phase content */}
              <div className="ml-4 flex-grow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {phase.phase?.title}
                  </h3>
                  <span className="text-sm font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {phase.period}
                  </span>
                </div>
                
                <div className="space-y-2">
                  {(phase.phase?.tasks || []).map((task, taskIndex) => (
                    <div key={taskIndex} className="flex items-start">
                      <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 mr-3"></div>
                      <p className="text-gray-700 text-sm">{task}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Progress indicator */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progresso Timeline</span>
          <span className="text-sm text-gray-500">0% completato</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-primary-600 h-2 rounded-full" style={{ width: '0%' }}></div>
        </div>
      </div>
    </div>
  );
};

export default RoadmapTimeline;